import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { usePermissions } from '@/hooks/usePermissions';
import { useTeamStore } from '@/stores/team-store';

/** Public storefront — works for anyone, from any team. */
export const SHOPIFY_PLAYER_URL =
  'https://shop.optisigns.com/products/optisigns-android-stick-player-2';

/** In-app Order Devices — only functional for a root admin in the default team. */
const ORDER_DEVICE_PATH = '/app/s/order-device';

/**
 * Where the welcome screen's hardware CTA ("Buy Now" / "Buy Devices") should go.
 *
 * Root admins in the default team — OWNER, SUPER_ADMIN and ADMIN (`isRootAdmin`) — get the in-app
 * Order Devices page, which bills to the account. Everyone else, and any team that is not the default
 * one, goes to the Shopify storefront, because `/app/s/order-device` renders nothing but "ask your
 * Admin to order devices in the default team" outside those conditions (#1904).
 *
 * `isRootAdmin` deliberately matches the OTHER Order Devices entry points (user menu, Settings
 * sidebar, Coupons, Screens toolbar) so a role that sees in-app ordering elsewhere is not sent to
 * Shopify from here.
 *
 * The CTA is therefore never hidden: the destination changes instead, so no one loses the ability to
 * buy hardware just because of their role or which team they are viewing.
 *
 * One hook for all three welcome arms (Classic / V2 / Test) so the rule cannot drift between them.
 */
export function useHardwareCta() {
  const navigate = useNavigate();
  const { isRootAdmin } = usePermissions();
  const isDefaultTeam = useTeamStore((s) => s.isDefaultTeam);

  const usesInAppOrdering = isRootAdmin && isDefaultTeam;

  const openHardwareCta = useCallback(() => {
    if (usesInAppOrdering) {
      navigate(ORDER_DEVICE_PATH);
      return;
    }
    // External target — noopener/noreferrer so the storefront cannot reach back via window.opener.
    window.open(SHOPIFY_PLAYER_URL, '_blank', 'noopener,noreferrer');
  }, [usesInAppOrdering, navigate]);

  return { openHardwareCta, usesInAppOrdering };
}
